import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth, anonymize } from "../App";
import {
  Scale, AlertTriangle, Calendar, ArrowRight, Search, BookOpen,
  ChevronDown, ChevronUp, Filter, FileText, Gavel, Tag
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const KpolD = () => {
  const { user } = useAuth();
  const [mainTab, setMainTab] = useState("search");
  const privacy = user?.privacy_mode || false;

  return (
    <div className="space-y-5 animate-fadeIn" data-testid="kpold-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Scale className="w-6 h-6 text-primary" />
            ΚΠολΔ
          </h1>
          <p className="text-slate-500 mt-1">Κώδικας Πολιτικής Δικονομίας & Έλεγχος Υποθέσεων</p>
        </div>
      </div>

      {/* Main Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-lg p-1" data-testid="kpold-main-tabs">
        <button
          onClick={() => setMainTab("search")}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
            mainTab === "search" ? "bg-white shadow text-slate-900" : "text-slate-500 hover:text-slate-700"
          }`}
          data-testid="tab-search"
        >
          <BookOpen className="w-4 h-4" />
          Κρίσιμα Άρθρα
        </button>
        <button
          onClick={() => setMainTab("all")}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
            mainTab === "all" ? "bg-white shadow text-slate-900" : "text-slate-500 hover:text-slate-700"
          }`}
          data-testid="tab-all"
        >
          <FileText className="w-4 h-4" />
          Όλα τα Άρθρα
        </button>
        <button
          onClick={() => setMainTab("cases")}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
            mainTab === "cases" ? "bg-white shadow text-slate-900" : "text-slate-500 hover:text-slate-700"
          }`}
          data-testid="tab-cases"
        >
          <Gavel className="w-4 h-4" />
          Έλεγχος Υποθέσεων
        </button>
      </div>

      {mainTab === "search" && <ArticleSearch criticalOnly={true} />}
      {mainTab === "all" && <ArticleSearch criticalOnly={false} />}
      {mainTab === "cases" && <CasesCheck privacy={privacy} />}
    </div>
  );
};

// ===================== ARTICLE SEARCH =====================
const ArticleSearch = ({ criticalOnly }) => {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [laws, setLaws] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [lawFilter, setLawFilter] = useState("");
  const [expandedArticle, setExpandedArticle] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchArticles(); }, [query, categoryFilter, lawFilter, criticalOnly]);

  const fetchArticles = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (query) params.append("q", query);
      if (categoryFilter && categoryFilter !== "all") params.append("category", categoryFilter);
      if (lawFilter && lawFilter !== "all") params.append("law", lawFilter);
      if (criticalOnly) params.append("critical_only", "true");
      const r = await axios.get(`${API}/kpold/search?${params.toString()}`);
      setArticles(r.data.articles || []);
      setCategories(r.data.categories || []);
      setLaws(r.data.laws || []);
    } catch { setArticles([]); }
    finally { setLoading(false); }
  };

  const toggleArticle = (num) => setExpandedArticle(expandedArticle === num ? null : num);

  const getCategoryLabel = (catId) => {
    const cat = categories.find(c => c.id === catId);
    return cat ? cat.title : catId;
  };

  const getCategoryColor = (catId) => {
    const colors = {
      general: "bg-slate-100 text-slate-700",
      taktiki: "bg-blue-100 text-blue-700",
      apodeixi: "bg-emerald-100 text-emerald-700",
      mikrodiafores: "bg-amber-100 text-amber-700",
      eidikes: "bg-purple-100 text-purple-700",
      endika: "bg-red-100 text-red-700",
      asfalistika: "bg-cyan-100 text-cyan-700",
      ekousia: "bg-orange-100 text-orange-700",
      ektelesi: "bg-pink-100 text-pink-700",
    };
    return colors[catId] || "bg-slate-100 text-slate-700";
  };

  return (
    <div className="space-y-4">
      {/* Search & Filters */}
      <Card>
        <CardContent className="pt-5 pb-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[250px]">
              <Label className="text-xs text-slate-500">Αναζήτηση (αριθμός/λέξη-κλειδί)</Label>
              <div className="relative">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                <Input
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder="π.χ. 237, προτάσεις, επίδοση, διαμεσολάβηση..."
                  className="pl-9"
                  data-testid="kpold-search-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Κατηγορία</Label>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-52" data-testid="kpold-category-filter">
                  <SelectValue placeholder="Όλες" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Όλες οι κατηγορίες</SelectItem>
                  {categories.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.title} ({c.range})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Τροποποίηση</Label>
              <Select value={lawFilter} onValueChange={setLawFilter}>
                <SelectTrigger className="w-40" data-testid="kpold-law-filter">
                  <SelectValue placeholder="Όλοι" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Όλοι οι νόμοι</SelectItem>
                  {laws.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results Count */}
      <div className="flex items-center gap-2">
        <Badge variant="secondary" data-testid="kpold-results-count">
          {articles.length} άρθρα
        </Badge>
        {criticalOnly && <Badge className="bg-amber-100 text-amber-700">Κρίσιμα για Γραμματεία</Badge>}
        {lawFilter && lawFilter !== "all" && <Badge className="bg-indigo-100 text-indigo-700">{lawFilter}</Badge>}
      </div>

      {/* Articles */}
      {loading ? (
        <div className="space-y-2">{[1,2,3,4].map(i => <Skeleton key={i} className="h-20 w-full" />)}</div>
      ) : articles.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <BookOpen className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-500 text-lg">Δεν βρέθηκαν άρθρα</p>
            <p className="text-slate-400 text-sm mt-1">Δοκιμάστε διαφορετική αναζήτηση</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {articles.map(article => (
            <Card key={article.number}
              className={`overflow-hidden transition-all ${expandedArticle === article.number ? "ring-2 ring-primary/30" : ""}`}
              data-testid={`article-${article.number}`}
            >
              <button
                className="w-full text-left p-4 hover:bg-slate-50/80 transition-colors"
                onClick={() => toggleArticle(article.number)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-lg font-bold text-primary">{article.number}</span>
                      <span className="font-semibold text-slate-800">{article.title}</span>
                      <Badge className={`text-[10px] px-1.5 py-0 ${getCategoryColor(article.category)}`}>
                        {getCategoryLabel(article.category)}
                      </Badge>
                      {article.amendments?.length > 0 && (
                        article.amendments.map((am, i) => (
                          <Badge key={i} variant="outline" className="text-[10px] px-1.5 py-0 border-indigo-300 text-indigo-600">
                            {am.law}
                          </Badge>
                        ))
                      )}
                    </div>
                    <p className="text-sm text-slate-500 mt-1 line-clamp-1">{article.summary}</p>
                  </div>
                  <div className="ml-3 flex-shrink-0">
                    {expandedArticle === article.number ?
                      <ChevronUp className="w-5 h-5 text-slate-400" /> :
                      <ChevronDown className="w-5 h-5 text-slate-400" />}
                  </div>
                </div>
              </button>

              {expandedArticle === article.number && (
                <div className="border-t bg-slate-50/50 p-4 space-y-4">
                  {/* Full Content */}
                  <div>
                    <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Περιεχόμενο</h4>
                    <div className="text-sm text-slate-700 whitespace-pre-line leading-relaxed bg-white p-4 rounded-lg border">
                      {article.content}
                    </div>
                  </div>

                  {/* Amendments */}
                  {article.amendments?.length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Τροποποιήσεις</h4>
                      <div className="space-y-2">
                        {article.amendments.map((am, i) => (
                          <div key={i} className="flex items-start gap-3 p-3 bg-indigo-50 rounded-lg border border-indigo-100">
                            <Badge className="bg-indigo-600 text-white flex-shrink-0 mt-0.5">{am.law}</Badge>
                            <p className="text-sm text-indigo-800">{am.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Keywords */}
                  {article.keywords?.length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <Tag className="w-3.5 h-3.5 text-slate-400" />
                      {article.keywords.map((kw, i) => (
                        <button key={i}
                          className="text-xs px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 hover:bg-primary/10 hover:text-primary transition-colors cursor-pointer"
                          onClick={(e) => { e.stopPropagation(); setQuery(kw); }}
                          data-testid={`keyword-${kw}`}
                        >{kw}</button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// ===================== CASES CHECK =====================
const CasesCheck = ({ privacy }) => {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [courtFilter, setCourtFilter] = useState("");

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchCases(); }, [courtFilter]);

  const fetchCases = async () => {
    try {
      setLoading(true);
      const params = courtFilter && courtFilter !== "all" ? `?court_type=${encodeURIComponent(courtFilter)}` : "";
      const r = await axios.get(`${API}/kpold/cases${params}`);
      setCases(r.data);
    } catch { setCases([]); }
    finally { setLoading(false); }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr + "T00:00:00").toLocaleDateString("el-GR", {
        weekday: "short", day: "numeric", month: "short", year: "numeric"
      });
    } catch { return dateStr; }
  };

  const privacyText = (text) => privacy ? anonymize(text) : text;

  return (
    <div className="space-y-4">
      {/* Info */}
      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-amber-800">Κρίσιμες Υποθέσεις</p>
            <p className="text-xs text-amber-600 mt-0.5">
              Υποθέσεις όπου η δικάσιμος απέχει &lt; 15 ημ. από ολοκλήρωση προδικαστικού ελέγχου.
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <Badge variant="secondary" data-testid="kpold-cases-count">{cases.length} υποθέσεις</Badge>
        <Select value={courtFilter} onValueChange={setCourtFilter}>
          <SelectTrigger className="w-44" data-testid="kpold-court-filter">
            <SelectValue placeholder="Όλοι οι τύποι" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Όλοι οι τύποι</SelectItem>
            <SelectItem value="Μονομελές">Μονομελές</SelectItem>
            <SelectItem value="Πολυμελές">Πολυμελές</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-24 w-full" />)}</div>
      ) : cases.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Scale className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-500 text-lg">Δεν βρέθηκαν υποθέσεις</p>
            <p className="text-slate-400 text-sm mt-1">Δεν υπάρχουν υποθέσεις στα κριτήρια ΚΠολΔ</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {cases.map(c => (
            <Card key={c.id} className="hover:ring-2 hover:ring-amber-200 transition-all" data-testid={`kpold-case-${c.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Link to={`/cases/${c.id}`} className="font-mono font-bold text-primary hover:underline text-base">
                        ΓΑΚ {c.gak_number}/{c.gak_year}
                      </Link>
                      <Badge variant="outline" className="text-xs font-mono">ΕΑΚ {c.eak_number || "—"}/{c.gak_year}</Badge>
                      <Badge className={c.court_type === "Μονομελές" ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"}>
                        {c.court_type}
                      </Badge>
                      <Badge className="bg-red-100 text-red-700">{c.days_between} ημ. διαφορά</Badge>
                    </div>
                    {c.subject && <p className="text-sm text-slate-600 mb-2">{c.subject}</p>}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                      <div>
                        <span className="text-slate-400">Ενάγοντες: </span>
                        <span className="text-slate-700">{privacyText(c.plaintiffs) || "-"}</span>
                      </div>
                      <div>
                        <span className="text-slate-400">Εναγόμενοι: </span>
                        <span className="text-slate-700">{privacyText(c.defendants) || "-"}</span>
                      </div>
                      <div>
                        <span className="text-slate-400">Δικάσιμος: </span>
                        <span className="font-medium text-emerald-700">{formatDate(c.hearing_date)}</span>
                      </div>
                      <div>
                        <span className="text-slate-400">Ολοκλ. Προδικαστικού: </span>
                        <span className="font-medium text-amber-700">{formatDate(c.pretrial_completion_date)}</span>
                      </div>
                    </div>
                  </div>
                  <Link to={`/cases/${c.id}`}>
                    <Button variant="ghost" size="sm"><ArrowRight className="w-4 h-4" /></Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default KpolD;
